import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Palette, Users, Sparkles, Calendar } from 'lucide-react';
import FeaturedCarousel, { CarouselSlide } from '../components/FeaturedCarousel';
import ArtworkGrid, { Artwork } from '../components/ArtworkGrid';
import { api } from '../lib/api';
import { getOptimizedImageUrl, getResponsiveSrcSet } from '../lib/cloudinary';
import type { Artist } from './Artists';

interface HomeContent {
  hero_slides: CarouselSlide[];
  featured_artwork_ids: string[];
  collections_heading: string;
  collections_subheading: string;
  featured_artists_heading: string;
  why_heading: string;
  why_subheading: string;
  why_items: { title: string; description: string }[];
  cta_heading: string;
  cta_subtext: string;
}

const WHY_ICONS = [Palette, Sparkles, Users];

export default function Home() {
  const [content, setContent] = useState<HomeContent | null>(null);
  const [collectionArtworks, setCollectionArtworks] = useState<Artwork[]>([]);
  const [featuredArtists, setFeaturedArtists] = useState<Artist[]>([]);
  const [latestPosts, setLatestPosts] = useState<any[]>([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [artworksLoading, setArtworksLoading] = useState(true);
  const [artistsLoading, setArtistsLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);

  useEffect(() => {
    api.getSiteContent('home').then(async (data: HomeContent) => {
      setContent(data);
      setContentLoading(false);
      if (data.featured_artwork_ids?.length) {
        api.getArtworksByIds(data.featured_artwork_ids)
          .then(setCollectionArtworks)
          .catch(console.error)
          .finally(() => setArtworksLoading(false));
      } else {
        api.getArtworks()
          .then((all: Artwork[]) => setCollectionArtworks(all.slice(0, 3)))
          .catch(console.error)
          .finally(() => setArtworksLoading(false));
      }
    }).catch(() => {
      setContentLoading(false);
      setArtworksLoading(false);
    });

    api.getFeaturedArtists()
      .then(setFeaturedArtists)
      .catch(console.error)
      .finally(() => setArtistsLoading(false));

    api.getPosts()
      .then((data: any[]) => setLatestPosts(data.slice(0, 3)))
      .catch(console.error)
      .finally(() => setPostsLoading(false));
  }, []);

  const heroSlides = (content?.hero_slides ?? []).filter((slide) => slide.image_url);
  const firstHeroImage = heroSlides[0]?.image_url
    ? getOptimizedImageUrl(heroSlides[0].image_url, 1280, 1080)
    : null;

  return (
    <div>
      <Helmet>
        <title>Global Black Art — Contemporary African &amp; Diaspora Art Gallery</title>
        <meta name="description" content="Discover and collect exceptional contemporary art by Black African and diaspora artists. Global Black Art curates paintings, sculptures, and mixed-media works from emerging and established creatives worldwide." />
        <meta property="og:title" content="Global Black Art — Contemporary African & Diaspora Art Gallery" />
        <meta property="og:description" content="Discover and collect exceptional contemporary art by Black African and diaspora artists. Global Black Art curates paintings, sculptures, and mixed-media works from emerging and established creatives worldwide." />
        <meta property="og:type" content="website" />
        {firstHeroImage && <link rel="preload" as="image" href={firstHeroImage} />}
      </Helmet>
      {contentLoading ? (
        <div className="relative w-full h-screen bg-gray-900 animate-pulse">
          <div className="absolute inset-0 flex items-end">
            <div className="w-full p-8 md:p-16">
              <div className="max-w-2xl space-y-5">
                <div className="h-3 w-28 bg-gray-700 rounded" />
                <div className="h-14 w-3/4 bg-gray-700 rounded" />
                <div className="h-6 w-1/2 bg-gray-700 rounded" />
                <div className="h-12 w-36 bg-gray-700 rounded" />
              </div>
            </div>
          </div>
        </div>
      ) : heroSlides.length > 0 ? (
        <FeaturedCarousel items={heroSlides} />
      ) : null}

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-gray-900 mb-4">
              {content?.collections_heading ?? 'Curated Collections'}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {content?.collections_subheading ?? 'Discover exceptional artworks from emerging and established artists worldwide'}
            </p>
          </div>

          {artworksLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-gray-200 mb-6" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : collectionArtworks.length > 0 ? (
            <ArtworkGrid items={collectionArtworks} columns={3} />
          ) : null}

          <div className="text-center mt-16">
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors"
            >
              Explore Full Collection
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight text-gray-900 mb-4">
              {content?.why_heading ?? 'Why Choose Our Gallery'}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {content?.why_subheading ?? 'Experience art with integrity and passion'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {(content?.why_items ?? [
              { title: 'Curated Selection', description: 'Each piece is carefully selected for its artistic merit and cultural significance' },
              { title: 'Authentic Works', description: 'Guaranteed authenticity with complete provenance documentation for every artwork' },
              { title: 'Expert Support', description: 'Knowledgeable art advisors ready to help you find the perfect pieces for your collection' },
            ]).map((item, i) => {
              const Icon = WHY_ICONS[i] ?? Palette;
              return (
                <div key={i} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 text-white rounded-lg mb-6">
                    <Icon size={32} />
                  </div>
                  <h3 className="text-2xl font-semibold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {(artistsLoading || featuredArtists.length > 0) && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-gray-900 mb-4">
                {content?.featured_artists_heading ?? 'Featured Artists'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {artistsLoading
                ? [...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-[3/4] bg-gray-200 rounded-lg mb-4" />
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                    </div>
                  ))
                : featuredArtists.map((artist) => (
                <Link key={artist._id} to={`/artists/${artist._id}`} className="group">
                  <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-4">
                    {artist.profile_image ? (
                      <img
                        src={getOptimizedImageUrl(artist.profile_image, 600)}
                        srcSet={getResponsiveSrcSet(artist.profile_image, [320, 480, 640, 800], 4 / 3)}
                        sizes="(max-width: 767px) 92vw, (max-width: 1023px) 45vw, (max-width: 1279px) 23vw, 23vw"
                        alt={artist.name}
                        width={600}
                        height={800}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <span className="text-gray-500 text-4xl font-light">
                          {artist.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-gray-600 transition-colors">
                    {artist.name}
                  </h3>
                  {artist.nationality && (
                    <p className="text-sm text-gray-500 mb-1">{artist.nationality}</p>
                  )}
                  {artist.bio && (
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{artist.bio}</p>
                  )}
                </Link>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/artists"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-900 text-gray-900 font-semibold hover:bg-gray-900 hover:text-white transition-colors"
              >
                View All Artists
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {(postsLoading || latestPosts.length > 0) && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-light tracking-tight text-gray-900 mb-4">
                From the Journal
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Insights, artist interviews, and curatorial notes from the gallery team
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {postsLoading
                ? [...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-white border border-gray-100 rounded-xl overflow-hidden">
                      <div className="aspect-[4/3] bg-gray-200" />
                      <div className="p-6 space-y-3">
                        <div className="h-3 bg-gray-200 rounded w-1/3" />
                        <div className="h-5 bg-gray-200 rounded w-full" />
                        <div className="h-5 bg-gray-200 rounded w-3/4" />
                        <div className="h-3 bg-gray-200 rounded w-full" />
                        <div className="h-3 bg-gray-200 rounded w-5/6" />
                      </div>
                    </div>
                  ))
                : latestPosts.map((post) => (
                <Link
                  key={post._id}
                  to={`/blog/${post.slug}`}
                  className="group block bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    {post.cover_image_url ? (
                      <img
                        src={getOptimizedImageUrl(post.cover_image_url, 800)}
                        srcSet={getResponsiveSrcSet(post.cover_image_url, [360, 540, 720, 900], 0.75)}
                        sizes="(max-width: 767px) 92vw, (max-width: 1023px) 46vw, 30vw"
                        alt={post.title}
                        width={800}
                        height={600}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-50">
                        <span className="text-gray-400 font-light tracking-widest text-sm uppercase">No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-3 font-semibold">
                      <Calendar size={13} strokeWidth={2} />
                      {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-gray-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 font-light leading-relaxed line-clamp-3 text-sm">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/blog"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-900 text-gray-900 font-semibold hover:bg-gray-900 hover:text-white transition-colors"
              >
                Read All Posts
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6">
            {content?.cta_heading ?? 'Ready to Start Your Collection?'}
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            {content?.cta_subtext ?? 'Connect with our team to find artworks that resonate with your vision and aesthetic'}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-3 bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-colors"
          >
            Get in Touch
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}